import { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ErrorMessage,
  fileReducer,
  FormatterOptions,
  GoogleFontIcon,
  Modal,
} from '@asset-metrix/portal-ui';
import { JsonError, JsonSchema } from '@sagold/react-json-editor';
import { MutationStatus } from '@tanstack/react-query';
import { z } from 'zod';

import { pixelSizeHelperPortalUi } from '../../utils/pixelSizeHelperPortalUi';

import { WorkflowActionModalInput } from './WorkflowActionModalInput';

import styles from './workflowActionModal.module.scss';

const UserDataType = z.union([z.string(), z.number(), z.boolean()]);
export type UserDataType = z.infer<typeof UserDataType>;

export type WorkflowActionModalProps = {
  closingSentence?: string;
  data: Record<string, unknown>[];
  errorMessage?: string;
  executionStatus?: MutationStatus[];
  fileUpload?: { required: boolean };
  formattingOptions?: Record<string, FormatterOptions | undefined>;
  introducingSentence?: string;
  onApply: (userData: Record<string, UserDataType>, files?: File[]) => void;
  onClose: () => void;
  opened: boolean;
  schema: JsonSchema;
  workflowActionModalState: MutationStatus;
};

function hasSupportedDataType(value: unknown): value is UserDataType {
  return UserDataType.safeParse(value).success;
}

function getInitialData(schema: JsonSchema, data: Record<string, unknown>[]) {
  function isReadOnly(key: string) {
    const property = schema.properties?.[key];
    return (
      typeof property === 'object' &&
      'options' in property &&
      property.options?.readOnly === true
    );
  }

  const result = new Map<string, unknown>();

  for (const row of data) {
    for (const [key, value] of Object.entries(row)) {
      if (!result.has(key) || result.get(key) === value) {
        result.set(key, value);
      } else if (isReadOnly(key)) {
        result.set(key, 'Multiple values');
      } else {
        result.set(key, undefined);
      }
    }
  }

  return Object.fromEntries(result.entries());
}

export function WorkflowActionModal({
  closingSentence,
  data,
  errorMessage,
  executionStatus,
  fileUpload,
  formattingOptions,
  introducingSentence,
  onApply,
  onClose,
  opened,
  schema,
  workflowActionModalState,
}: WorkflowActionModalProps) {
  const initialData = useMemo(
    () => getInitialData(schema, data),
    [schema, data]
  );
  const { t } = useTranslation();
  const [validationErrors, setValidationErrors] = useState<JsonError[]>([]);
  const [editorData, setEditorData] = useState(initialData);
  const [fileSelections, dispatch] = useReducer(fileReducer, []);

  useEffect(() => {
    // Reset state of files in case of error, just like
    // rest of the modal.
    if (workflowActionModalState === 'error') {
      dispatch({
        type: 'updateAllUploadStates',
        payload: {
          newState: 'initial',
        },
      });
    }
  }, [workflowActionModalState]);

  const enhancedSchema = useMemo(
    () => ({
      ...schema,
      options: {
        ...schema.options,
        showTitle: false,
        showErrors: false,
      },
    }),
    [schema]
  );

  const apply = useCallback(() => {
    if (validationErrors.length > 0) {
      console.error(
        'Cannot apply workflow action modal due to validation errors.',
        validationErrors
      );
      return;
    }

    dispatch({
      type: 'updateAllUploadStates',
      payload: {
        newState: 'isPending',
      },
    });

    const changedData: Record<string, UserDataType> = {};

    for (const [key, value] of editorData != null
      ? Object.entries(editorData)
      : []) {
      const propertyDefinition = schema.properties?.[key];
      if (
        propertyDefinition != null &&
        typeof propertyDefinition != 'boolean' &&
        !propertyDefinition?.options?.readOnly &&
        hasSupportedDataType(value)
      ) {
        changedData[key] = value;
      }
    }

    const files =
      fileUpload != null ? fileSelections.map((f) => f.file) : undefined;

    onApply(changedData, files);
  }, [
    editorData,
    fileSelections,
    fileUpload,
    onApply,
    schema.properties,
    validationErrors,
  ]);

  const applyButtonLabel =
    typeof schema.applyButtonLabel === 'string' &&
    schema.applyButtonLabel.length > 0
      ? schema.applyButtonLabel
      : t('app.table.workflowActionModal.apply');

  return (
    <Modal
      actions={
        <>
          <Button
            variant="secondary"
            icon={<GoogleFontIcon name="close" />}
            onClick={() => onClose()}
          >
            {t('app.table.workflowActionModal.cancel')}
          </Button>
          <Button
            disabled={
              validationErrors.length > 0 ||
              (Boolean(fileUpload?.required) && fileSelections.length === 0)
            }
            loading={workflowActionModalState === 'pending'}
            variant="primary"
            onClick={apply}
          >
            {applyButtonLabel}
          </Button>
        </>
      }
      size={pixelSizeHelperPortalUi(650)}
      modalType="action"
      opened={opened}
      onClose={onClose}
      title={schema.title}
    >
      <div className={styles['content']}>
        <WorkflowActionModalInput
          data={initialData}
          disabled={workflowActionModalState === 'pending'}
          fileUpload={
            fileUpload && {
              ...fileUpload,
              dispatch,
              selections: fileSelections,
            }
          }
          formattingOptions={formattingOptions}
          onChange={(data, errors) => {
            setEditorData(data);
            setValidationErrors(errors);
          }}
          schema={enhancedSchema}
          closingSentence={closingSentence}
          introducingSentence={introducingSentence}
        />
        {workflowActionModalState === 'error' && (
          <ErrorMessage className={styles['message']}>
            {errorMessage}
          </ErrorMessage>
        )}
        {(executionStatus ?? []).length > 1 &&
          executionStatus?.includes('error') && (
            <ErrorMessage className={styles['message']}>
              {t(
                'app.table.workflowActionModal.actionSuccessfulCountTimes',
                '',
                {
                  count: executionStatus?.filter(
                    (status) => status === 'success'
                  ).length,
                }
              ) +
                ' ' +
                t('app.table.workflowActionModal.actionFailedCountTimes', '', {
                  count: executionStatus?.filter((status) => status === 'error')
                    .length,
                })}
            </ErrorMessage>
          )}
      </div>
    </Modal>
  );
}
